// @ts-strict

import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { ISingleCampaign, ICountry as CountryItem, SpecialCampaignData } from '@app/core/models/core-models';
import { ScrollableTabMenuItem } from '@app/shared/components/scrollable-tab-menu/scrollable-tab-menu.component';
import { TranslateService } from '@ngx-translate/core';

import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { distinctUntilChanged, tap } from 'rxjs/operators';

type ICountry = Record<
  string,
  {
    firstLetter: string;
    countries: TranslatedCountry[];
  }
>;

interface IMenuItem {
  letter: string;
  active: boolean;
}

interface TranslatedCountry extends CountryItem {
  translation: string;
}

@Component({
  selector: 'app-countries-modal',
  templateUrl: './countries-modal.component.html',
  styleUrls: ['./countries-modal.component.scss']
})
export class CountriesModalComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @ViewChild('countriesList') countriesList?: ElementRef<HTMLElement>;

  @Input() watchInactiveCountries: boolean = false;
  @Input() currentCampaign?: ISingleCampaign | SpecialCampaignData;
  @Input() onCountryClick?: (isActive: boolean, countryId?: number) => void;

  public tabMenuItems: ScrollableTabMenuItem[] = [];

  public campaignCountryList: TranslatedCountry[] = [];
  private countryListBeforeSearch: TranslatedCountry[] = [];
  public countryList: ICountry = {};
  private activeLetters: string[] = [];
  private menuItems: IMenuItem[] = [];
  private scrollPositions: number[] = [];
  public searchFocus = false;
  public searchValue = '';
  public activeLetter = 'a';

  public loading = false;

  public searchVal = new BehaviorSubject<string>('');

  private destroy$ = new Subject<void>();

  constructor(private readonly translate: TranslateService) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.initializeCountryList();
  }

  ngOnInit(): void {
    this.loading = true;

    this.initializeCountryList();
    this.searchVal
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        tap(searchValue => this.handleSearch(searchValue))
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.addObserverToElements();
    }, 500);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.listScrollObserver.disconnect();
  }

  private observerCallback: IntersectionObserverCallback = entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this.tabMenuItems = this.tabMenuItems.map(({ active, name, ...rest }) => ({ active: entry.target.id.replace('letter_', '') === name, name, ...rest }));
      }
    });
  };

  private listScrollObserver = new IntersectionObserver(this.observerCallback, {
    root: this.countriesList?.nativeElement,
    rootMargin: '0px',
    threshold: 0.5
  });

  private addObserverToElements(): void {
    const container = this.countriesList?.nativeElement;

    if (container) {
      const menuItemElements = this.menuItems
        .map(item => container.querySelector(`#letter_${item.letter.toLocaleLowerCase()}`))
        .filter(item => item !== null) as HTMLElement[];

      menuItemElements.forEach(item => this.listScrollObserver?.observe(item));
    }
  }

  private initializeCountryList(): void {
    if (this.currentCampaign?.countries) {
      this.campaignCountryList = this.currentCampaign.countries.map(el => ({ ...el, translation: '' }));
    }

    this.getFirstLetters();
  }

  private handleSearch(searchValue: string): void {
    const tempCountryList: TranslatedCountry[] = this.countryListBeforeSearch;

    if (tempCountryList) {
      if (!searchValue) {
        this.campaignCountryList = tempCountryList;
      }

      this.campaignCountryList = tempCountryList.filter(item => item.translation.toLowerCase().includes(searchValue.toLowerCase()));

      this.resetCountryList();
      this.getFirstLetters();
    }
  }

  private resetCountryList(): void {
    this.countryList = {};
    this.activeLetters = [];
    this.menuItems = [];
    this.scrollPositions = [];
  }

  public searchValHandler = (value: string) => {
    this.searchVal.next(value);
  };

  public scroll(element: ScrollableTabMenuItem): void {
    this.activeLetter = element.name;
    this.activeMenuItem(element.name);

    const selectedElement = document.getElementById(`letter_${element.name}`);
    const parentElement = document.getElementById('allCountriesGroup');

    if (selectedElement && parentElement) {
      parentElement.scrollTo(0, selectedElement.offsetTop - parentElement.offsetTop);
    }
  }

  public clearInput(): void {
    this.searchValue = '';
  }

  public onCountryClickHandler = (isActive: boolean, countryId: number) => {
    if (typeof this.onCountryClick === 'function') {
      const updatedCountries = this.currentCampaign?.countries?.map(country => (country.id === countryId ? { ...country, active: isActive } : country));
      this.currentCampaign = { ...(this.currentCampaign || {}), countries: updatedCountries || [] };
      this.initializeCountryList();
      return this.onCountryClick(isActive, countryId);
    }
  };

  public trackCountry = (_index: number, country: CountryItem) => {
    return country.id;
  };

  private getFirstLetters(): void {
    this.campaignCountryList.forEach(item => {
      item.translation = this.translate.instant(`countries.${item.id - 1}`).name;

      if (this.countryList) {
        if (!this.activeLetters.includes(item.translation.slice(0, 1).toLocaleLowerCase())) {
          this.activeLetters.push(item.translation.slice(0, 1).toLocaleLowerCase());
        }
        if (!(`${item.translation.slice(0, 1)}` in this.countryList)) {
          this.countryList[item.translation.slice(0, 1).toLocaleLowerCase()] = {
            firstLetter: item.translation.slice(0, 1).toLocaleLowerCase(),
            countries: []
          };
        }
      }
    });

    if (!this.countryListBeforeSearch.length) {
      this.countryListBeforeSearch = this.campaignCountryList;
    }

    this.assignCountryByFirstLetter();

    this.setTabMenuItems(this.countryList);
    this.prepareMenuItems();

    setTimeout(() => {
      this.loading = false;
    }, 500);
  }

  private assignCountryByFirstLetter(): void {
    this.campaignCountryList.forEach(item => {
      const key = item.translation.slice(0, 1).toLocaleLowerCase();

      if (!(key in this.countryList)) {
        this.countryList[key] = { firstLetter: key, countries: [] };
      }

      this.countryList[key].countries.push(item);
    });
  }

  private prepareMenuItems(): void {
    this.activeLetters.forEach(letter => {
      this.menuItems.push({ letter, active: false });
    });
  }

  private activeMenuItem(name?: string): void {
    if (!name) {
      this.menuItems = this.menuItems.map(({ letter }, i) => ({ letter, active: i === 0 }));

      return;
    }

    this.menuItems = this.menuItems.map(({ letter }) => ({ letter, active: letter === name }));
  }

  private setTabMenuItems = (countryList: ICountry): void => {
    this.tabMenuItems = Object.values(countryList).map(({ firstLetter, countries }, i) => ({
      text: firstLetter,
      name: firstLetter,
      active: i === 0,
      count: countries.length
    }));
  };
}
